import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';

const NAV_ITEMS = [
  {
    label: 'Questionnaire',
    path: '/quiz-participation-interface',
    icon: 'PlayCircle',
    tooltip: 'Remplir le QCM',
  },
  {
    label: 'Résultats',
    path: '/results-and-download-center',
    icon: 'BarChart2',
    tooltip: 'Consulter les résultats',
  },
];

const TabNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tooltipVisible, setTooltipVisible] = useState(null);

  const isActive = (path) => location?.pathname === path || location?.pathname?.startsWith(path);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleKeyDown = (e, path, index) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      handleNavigate(path);
    }
    if (e?.key === 'ArrowRight') {
      e?.preventDefault();
      const next = (index + 1) % NAV_ITEMS?.length;
      document.getElementById(`tab-${next}`)?.focus();
    }
    if (e?.key === 'ArrowLeft') {
      e?.preventDefault();
      const prev = (index - 1 + NAV_ITEMS?.length) % NAV_ITEMS?.length;
      document.getElementById(`tab-${prev}`)?.focus();
    }
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav
        className="tab-nav hidden md:flex"
        role="navigation"
        aria-label="Navigation principale"
      >
        <div className="flex items-center w-full max-w-7xl mx-auto px-6">
          {/* Brand */}
          <div className="flex items-center gap-3 mr-8 flex-shrink-0">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              <Icon name="BookOpen" size={18} color="var(--color-primary-foreground)" strokeWidth={2} />
            </div>
            <span
              className="font-semibold text-lg"
              style={{ color: 'var(--color-text-primary)' }}
            >
              QCM ISO 27001
            </span>
          </div>

          {/* Tabs */}
          <div
            className="flex items-center gap-1"
            role="tablist"
            aria-label="Phases du quiz"
          >
            {NAV_ITEMS?.map((item, index) => (
              <div key={item?.path} className="relative">
                <button
                  id={`tab-${index}`}
                  role="tab"
                  aria-selected={isActive(item?.path)}
                  aria-label={item?.tooltip}
                  tabIndex={isActive(item?.path) ? 0 : -1}
                  className={`tab-item ${isActive(item?.path) ? 'active' : ''}`}
                  onClick={() => handleNavigate(item?.path)}
                  onKeyDown={(e) => handleKeyDown(e, item?.path, index)}
                  onMouseEnter={() => setTooltipVisible(index)}
                  onMouseLeave={() => setTooltipVisible(null)}
                  onFocus={() => setTooltipVisible(index)}
                  onBlur={() => setTooltipVisible(null)}
                >
                  <Icon
                    name={item?.icon}
                    size={16}
                    color={isActive(item?.path) ? 'var(--color-primary)' : 'currentColor'}
                    strokeWidth={isActive(item?.path) ? 2.5 : 2}
                  />
                  <span>{item?.label}</span>
                </button>

                {/* Tooltip */}
                {tooltipVisible === index && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 rounded text-xs font-caption whitespace-nowrap pointer-events-none z-50"
                    style={{
                      backgroundColor: 'var(--color-secondary)',
                      color: 'var(--color-secondary-foreground)',
                      boxShadow: '0 2px 6px rgba(15,23,42,0.12)',
                    }}
                    role="tooltip"
                  >
                    {item?.tooltip}
                    <div
                      className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
                      style={{ backgroundColor: 'var(--color-secondary)' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Date indicator */}
          <span
            className="font-mono text-xs hidden lg:block"
            style={{ color: 'var(--color-muted-foreground)' }}
          >
            {new Date()?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        </div>
      </nav>
      {/* Mobile Bottom Tab Bar */}
      <nav
        className="tab-nav-mobile flex md:hidden"
        role="navigation"
        aria-label="Navigation principale mobile"
      >
        <div
          className="flex items-stretch w-full"
          role="tablist"
          aria-label="Phases du quiz"
        >
          {NAV_ITEMS?.map((item, index) => (
            <button
              key={item?.path}
              id={`tab-mobile-${index}`}
              role="tab"
              aria-selected={isActive(item?.path)}
              aria-label={item?.label}
              className={`tab-item-mobile ${isActive(item?.path) ? 'active' : ''}`}
              onClick={() => handleNavigate(item?.path)}
              onKeyDown={(e) => handleKeyDown(e, item?.path, index)}
            >
              <Icon
                name={item?.icon}
                size={20}
                color={isActive(item?.path) ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                strokeWidth={isActive(item?.path) ? 2.5 : 2}
              />
              <span>{item?.label}</span>
            </button>
          ))}
        </div>
      </nav>
      {/* Mobile Top Brand Bar */}
      <div
        className="flex md:hidden items-center px-4 h-[60px] fixed top-0 left-0 right-0 z-[100]"
        style={{
          backgroundColor: 'var(--color-card)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
        }}
      >
        <div
          className="w-7 h-7 rounded flex items-center justify-center mr-2"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          <Icon name="BookOpen" size={15} color="var(--color-primary-foreground)" strokeWidth={2} />
        </div>
        <span
          className="font-semibold text-base"
          style={{ color: 'var(--color-text-primary)' }}
        >
          QCM ISO 27001
        </span>
      </div>
    </>
  );
};

export default TabNavigation;